/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useApp } from '@/contexts/AppContext';
import { Download, Loader2, Check, RefreshCw } from 'lucide-react';
import {
  installMcpServer,
  installSkill,
  isInstallableAgent,
  toInstallableAgent,
  getAgentDisplayName,
  type InstallResult,
} from '@/services/skillshop';
import { InstallCopyDialog } from './InstallCopyDialog';

export interface InstallButtonProps {
  /** Type of item to install */
  type: 'mcp' | 'skill';
  /** Server ID (for MCP) or skill path (for Skills) */
  itemId: string;
  /** Display name for the item */
  itemName: string;
  /** Compact mode for list cards (icon only) */
  compact?: boolean;
  /** Callback when installation completes */
  onInstalled?: () => void;
}

/**
 * Install to Agent Button
 * 
 * Shows an install button that adapts to the user's selected coding agent.
 * Hidden when the agent is Gemini (no standardized install mechanism).
 * Falls back to a copy dialog on install failure.
 */
export function InstallButton({ 
  type, 
  itemId, 
  itemName, 
  compact = false,
  onInstalled,
}: InstallButtonProps) {
  const { codingAgent } = useApp();
  const { toast } = useToast();
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [installError, setInstallError] = useState<string | null>(null);
  const [isReinstall, setIsReinstall] = useState(false);

  // Don't show button for Gemini
  const installableAgent = toInstallableAgent(codingAgent);
  if (!installableAgent) {
    return null;
  }

  const agentDisplayName = getAgentDisplayName(installableAgent);

  // Install mutation
  const installMutation = useMutation({
    mutationFn: async ({ overwrite }: { overwrite?: boolean } = {}) => {
      if (type === 'mcp') {
        return installMcpServer(itemId, installableAgent, overwrite);
      } else {
        return installSkill(itemId, installableAgent, overwrite);
      }
    },
    onSuccess: (result: InstallResult) => {
      if (result.alreadyInstalled && !isReinstall) {
        // Show option to reinstall
        toast({
          title: 'Already Installed',
          description: `"${itemName}" is already installed in ${agentDisplayName}. Click again to reinstall.`,
        });
        setIsReinstall(true);
      } else {
        // Success
        toast({
          title: 'Installed Successfully',
          description: result.message,
        });
        setIsReinstall(false);
        onInstalled?.();
      }
    },
    onError: (error: Error & { details?: { configSnippet?: string; instructions?: string } }) => {
      console.error('Install failed:', error);
      setInstallError(error.message);
      setShowCopyDialog(true);
      toast({
        title: 'Installation Failed',
        description: 'Click "Copy Config" to install manually.',
        variant: 'destructive',
      });
    },
  });

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    installMutation.mutate({ overwrite: isReinstall });
  };

  const isLoading = installMutation.isPending;

  // Determine button content
  const getButtonContent = () => {
    if (isLoading) {
      return (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          {!compact && <span className="ml-1">Installing...</span>}
        </>
      );
    }

    if (isReinstall) {
      return (
        <>
          <RefreshCw className="h-4 w-4" />
          {!compact && <span className="ml-1">Reinstall</span>}
        </>
      );
    }

    return (
      <>
        <Download className="h-4 w-4" />
        {!compact && <span className="ml-1">Install to {agentDisplayName}</span>}
      </>
    );
  };

  return (
    <>
      <Button
        variant={isReinstall ? 'outline' : 'default'}
        size={compact ? 'sm' : 'default'}
        onClick={handleClick}
        disabled={isLoading}
        className={compact ? 'h-8 w-8 p-0' : ''}
        title={compact ? `Install to ${agentDisplayName}` : undefined}
      >
        {getButtonContent()}
      </Button>

      <InstallCopyDialog
        open={showCopyDialog}
        onOpenChange={setShowCopyDialog}
        type={type}
        itemId={itemId}
        itemName={itemName}
        agent={installableAgent}
        error={installError}
      />
    </>
  );
}

/**
 * Compact install button for card layouts
 */
export function InstallButtonCompact(props: Omit<InstallButtonProps, 'compact'>) {
  return <InstallButton {...props} compact />;
}
